import React, { useState } from 'react'
import {
    Flex,
    Icon,
    InputGroup,
    InputLeftElement,
    Select,
    Text,
} from '@chakra-ui/react'
import { FiInfo } from 'react-icons/fi'
import { format } from 'date-fns'

import { useClinic } from '~contexts/clinic-context'
import { formatCents, getClientFullName } from '~utils/helpers'
import { useSearchClients } from '~utils/hooks/use-search-clients'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import {
    InvoiceFieldsFragment,
    InvoiceStatus,
    useCreatePaymentLinkMutation,
    useQueryInvoicesQuery,
} from '~graphql/generated/graphql'
import { eqStringFilter, gtFloatFilter } from '~utils/query-helpers'
import { constructOrderBy } from '~utils/querying'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedInfoText from '~components/shared/texts/shared-info-text'
import SharedHelpText from '~components/shared/form/shared-help-text'
import { FtsSelect } from '~components/shared/form/fts-select'
import SharedTextarea from '~components/shared/form/shared-textarea'
import SharedNumberInput from '~components/shared/form/shared-number-input'
import ClientInfoTooltip from '~components/shared/client/client-info-tooltip'

const MIN_AMOUNT_CENTS = 50
const MAX_DESCRIPTION_LENGTH = 500

interface Props {
    isOpen: boolean
    onClose: () => void
    /**
     * When provided (inbox), the link is created for this client and the
     * client search is hidden. Omit it (payments page) to pick a client.
     */
    clientId?: string | null
    /** Appointment to attach, e.g. the one the chat was opened from. */
    appointmentId?: string
    /**
     * Invoice to link, e.g. from the invoice drawer. Locks the invoice
     * picker and prefills amount + description. Omit to pick (or skip)
     * an invoice inside the modal.
     */
    presetInvoice?: InvoiceFieldsFragment | null
    /** Called with the checkout URL after a link is created. */
    onCreated?: (url: string) => void
}

/**
 * Creates a staff-generated text-to-pay link. Render gated on `isOpen`
 * (`{isOpen && <PaymentLinkModal .../>}`) so the client search only runs
 * while the modal is open and form state resets between opens.
 */
export default function PaymentLinkModal({
    isOpen,
    onClose,
    clientId,
    appointmentId,
    presetInvoice,
    onCreated,
}: Props) {
    const { clinic } = useClinic()
    const toasts = useToastFeedback(
        'Payment link created.',
        'Error creating payment link.',
    )

    const [clientQuery, setClientQuery] = useState('')
    const [selectedClientId, setSelectedClientId] = useState<string | null>(
        null,
    )
    const [amountDollars, setAmountDollars] = useState(
        presetInvoice?.balance ? presetInvoice.balance / 100 : 0,
    )
    const [description, setDescription] = useState(
        presetInvoice?.description?.slice(0, MAX_DESCRIPTION_LENGTH) ?? '',
    )
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
        presetInvoice?.id ?? null,
    )

    const {
        clientResults,
        loading: isSearchLoading,
        hasMore: hasMoreSearchResults,
        setPage: setSearchPage,
    } = useSearchClients({
        clinic,
        query: clientQuery,
        shouldAccumulatePages: true,
    })

    const effectiveClientId = clientId ?? selectedClientId
    const amountCents = Math.round((amountDollars || 0) * 100)
    const trimmedDescription = description.trim()

    const { data: invoicesData, loading: invoicesLoading } =
        useQueryInvoicesQuery({
            variables: {
                where: [
                    eqStringFilter('clinic', clinic?.id || ''),
                    eqStringFilter('client', effectiveClientId || ''),
                    eqStringFilter('status', InvoiceStatus.Active),
                    gtFloatFilter('balance', 0),
                ],
                page: 0,
                pageSize: 50,
                orderBy: constructOrderBy('pims_created_at', false),
            },
            skip: !clinic?.id || !effectiveClientId || !!presetInvoice,
        })

    const invoices = presetInvoice
        ? [presetInvoice]
        : invoicesData?.queryInvoices || []
    const selectedInvoice =
        invoices.find(invoice => invoice.id === selectedInvoiceId) ?? null
    const isOverBalance =
        selectedInvoice?.balance != null &&
        amountCents > selectedInvoice.balance

    const isValid =
        !!clinic?.id &&
        !!effectiveClientId &&
        amountCents >= MIN_AMOUNT_CENTS &&
        trimmedDescription.length > 0 &&
        trimmedDescription.length <= MAX_DESCRIPTION_LENGTH &&
        !isOverBalance

    const [createPaymentLink, { loading }] = useCreatePaymentLinkMutation({
        onCompleted: async data => {
            const url =
                data.createPaymentLink.stripe_checkout_session_url

            if (!url) {
                toasts.onError(
                    'The payment was created but no link was returned.',
                )
                return
            }

            try {
                await navigator.clipboard.writeText(url)
                toasts.onCompleted(
                    'Payment link created and copied to clipboard.',
                )
            } catch {
                toasts.onCompleted(
                    'Payment link created. Copy it from the payments table.',
                )
            }

            onCreated?.(url)
            onClose()
        },
        onError: error => toasts.onError(error.message),
    })

    function handleConfirm() {
        if (!clinic?.id || !effectiveClientId || !isValid) {
            return
        }

        createPaymentLink({
            variables: {
                data: {
                    clinic: clinic.id,
                    client: effectiveClientId,
                    amount: amountCents,
                    description: trimmedDescription,
                    ...(appointmentId ? { appointment: appointmentId } : {}),
                    ...(selectedInvoiceId
                        ? { invoice: selectedInvoiceId }
                        : {}),
                },
            },
        })
    }

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            onConfirm={handleConfirm}
            confirmText="Create link"
            title="New payment link"
            isConfirmDisabled={!isValid}
            isLoading={loading}
            skipCloseOnConfirm
        >
            <SharedStack gap={6}>
                {!clientId && (
                    <SharedStack>
                        <SharedInfoText>Client</SharedInfoText>
                        <FtsSelect
                            options={clientResults}
                            filteredOptions={clientResults}
                            selectedOptions={clientResults.filter(
                                r => r.client?.id === selectedClientId,
                            )}
                            setSelectedOptions={selected => {
                                const last =
                                    selected[selected.length - 1]
                                setSelectedClientId(
                                    last?.client?.id ?? null,
                                )
                                // Invoices belong to one client; a new
                                // client starts unlinked.
                                setSelectedInvoiceId(null)
                            }}
                            displayTransformer={o => (
                                <ClientInfoTooltip client={o.client}>
                                    <Flex alignItems="center">
                                        {getClientFullName(o.client)}
                                        <Icon as={FiInfo} pl={1} />
                                    </Flex>
                                </ClientInfoTooltip>
                            )}
                            setSearch={setClientQuery}
                            placeholder="Search for clients"
                            isLoading={isSearchLoading}
                            hasMore={hasMoreSearchResults}
                            onScrolledToBottom={() =>
                                setSearchPage(page => page + 1)
                            }
                        />
                    </SharedStack>
                )}
                <SharedStack>
                    <SharedInfoText>
                        Invoice{' '}
                        <Text
                            as="span"
                            fontWeight="normal"
                            color="gray.500"
                        >
                            (optional)
                        </Text>
                    </SharedInfoText>
                    {presetInvoice ? (
                        <Text fontWeight="medium">
                            {formatInvoiceOption(presetInvoice)}
                        </Text>
                    ) : (
                        <Select
                            placeholder={
                                !effectiveClientId
                                    ? 'Select a client first'
                                    : invoicesLoading
                                      ? 'Loading invoices...'
                                      : 'No invoice — apply to account balance'
                            }
                            value={selectedInvoiceId ?? ''}
                            onChange={e => {
                                const nextId = e.target.value || null
                                setSelectedInvoiceId(nextId)
                                const next = invoices.find(
                                    invoice => invoice.id === nextId,
                                )
                                // Default to the full balance due; staff can
                                // lower it for a partial payment.
                                if (next?.balance) {
                                    setAmountDollars(next.balance / 100)
                                }
                            }}
                            isDisabled={
                                !effectiveClientId || invoicesLoading
                            }
                        >
                            {invoices.map(invoice => (
                                <option key={invoice.id} value={invoice.id}>
                                    {formatInvoiceOption(invoice)}
                                </option>
                            ))}
                        </Select>
                    )}
                    {isOverBalance && selectedInvoice?.balance != null ? (
                        <SharedHelpText mb={0} color="red.500">
                            Amount is more than the invoice balance due (
                            {formatCents(selectedInvoice.balance)}).
                        </SharedHelpText>
                    ) : (
                        <SharedHelpText mb={0}>
                            {effectiveClientId &&
                            !invoicesLoading &&
                            invoices.length === 0
                                ? 'No unpaid invoices — the payment will apply to the account balance.'
                                : 'Linked payments apply to the invoice in your PMS. Partial payments are allowed.'}
                        </SharedHelpText>
                    )}
                </SharedStack>
                <SharedStack>
                    <SharedInfoText>Amount</SharedInfoText>
                    <InputGroup>
                        <InputLeftElement
                            pointerEvents="none"
                            color="gray.500"
                            fontSize="sm"
                        >
                            $
                        </InputLeftElement>
                        <SharedNumberInput
                            value={amountDollars}
                            onChange={(_valueAsString, valueAsNumber) =>
                                setAmountDollars(
                                    isNaN(valueAsNumber) ? 0 : valueAsNumber,
                                )
                            }
                            min={0}
                            precision={2}
                            containerProps={{ w: 'full' }}
                            inputFieldProps={{ paddingLeft: 10 }}
                        />
                    </InputGroup>
                    <SharedHelpText mb={0}>
                        Minimum $0.50. Your clinic&apos;s standard processing
                        fees may be added at checkout.
                        {selectedInvoice?.balance != null &&
                            ` ${formatCents(
                                selectedInvoice.balance,
                            )} due on the linked invoice.`}
                    </SharedHelpText>
                </SharedStack>
                <SharedStack>
                    <SharedInfoText>Description</SharedInfoText>
                    <SharedTextarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="e.g. Dental cleaning deposit"
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        rows={2}
                    />
                    <SharedHelpText mb={0}>
                        Shown to the client at checkout.{' '}
                        {description.length}/{MAX_DESCRIPTION_LENGTH}
                    </SharedHelpText>
                </SharedStack>
                <SharedHelpText mb={0}>
                    Links are single-use and expire 24 hours after creation if
                    unpaid.
                </SharedHelpText>
            </SharedStack>
        </SharedModal>
    )
}

function formatInvoiceOption(invoice: InvoiceFieldsFragment): string {
    const date = format(
        invoice.pims_created_at || invoice.created_at,
        'yyyy-MM-dd',
    )
    const label = invoice.description || 'Invoice'
    const due = formatCents(invoice.balance || 0)
    return `${date} · ${label} · ${due} due`
}
